import { successSession } from './sessionSlice'
import { successOrderCreate } from './checkoutSlice'
import { createSlice, createSelector } from '@reduxjs/toolkit'
import { successLogin } from './userSlice'
import { getUniqItems } from '../../utils'

export const idsObjArrayToIdsObj = (ids, value = true) => ids?.reduce((acc, el) => {
  Object.values(el).forEach(id => {
    if (id) {
      acc[id] = value;
    }
  })
  return acc;
}, {}) || {};

const initialState = {
  products: [],
  links: {},
  meta: {},
  count: 0,
  firstLoading: false,
  ids: [],
  idsObj: {},
  loading: false,
  updating: false,
  syncTimeoutId: null,
  error: null,
  updateError: null,
}

const wishlist = createSlice({
  name: 'wishlist',
  initialState: initialState,
  reducers: {
    requestWishlist(state) {
      state.loading = true
      state.error = null
      state.updateError = null
    },
    successWishlist(state, { payload }) {
      const mergeResult = payload?.mergeResult ?? false

      if (mergeResult) {
        const result = [...state.products, ...(payload?.favorites ?? [])]
        state.products = getUniqItems(result, item => item.id)
      } else {
        state.products = payload?.favorites ?? []
      }

      state.meta = payload?.meta ?? {}
      state.links = payload?.links ?? {}
      state.firstLoading = false

      state.loading = false
      state.error = null
    },
    firstRequestWishlist(state) {
      state.firstLoading = true
    },
    failureWishlist(state, { payload }) {
      state.loading = false
      state.firstLoading = false
      state.error = payload
    },
    updateIds(state, { payload }) {
      state.ids = payload
      state.count = payload.length
    },
    updateIdsObj(state, { payload }) {
      state.idsObj = payload
    },
    cancelSync(state) {
      clearTimeout(state.syncTimeoutId)
    },
    successRequestWishlist(state) {
      state.loading = false
    },
    resetWishlist() {
      return { ...initialState }
    },

    startUpdatingWishlist(state) {
      state.updating = true
      state.updateError = null
    },
    successUpdatingWishlist(state) {
      state.updating = false
    },
    failureUpdatingWishlist(state, { payload }) {
      state.updating = false
      state.updateError = payload
    },
  },
  extraReducers: {
    [successSession]: _getProductsFromSession,
    [successLogin]: _getProductsFromSession,
    [successOrderCreate]: _getProductsFromSession,
  },
})

function _getProductsFromSession(state, { payload }) {
  const favorites = payload?.favorites ?? null
  const favorites_count = payload?.favorites_count ?? null

  if (favorites) {
    state.ids = favorites
    state.idsObj = idsObjArrayToIdsObj(favorites)
  }
  if (favorites_count !== null) {
    state.count = favorites_count
  }
}

export const setLoadingRequest = () => async (dispatch, getState, { api }) => {
  dispatch(firstRequestWishlist())
}

export const fetchWishlist =
  ({ onSuccess, page, mergeResult, errorToString = true }) =>
    async (dispatch, getState, { api }) => {
      dispatch(requestWishlist())
      try {
        const resp = await api.wishlist({ page })
        const { result = 'success', data, links, meta } = resp
        if (result === 'success') {
          dispatch(successWishlist({ favorites: data, links, meta, mergeResult }))
          onSuccess?.(data)
        } else {
          dispatch(failureWishlist(resp))
        }
      } catch (err) {
        dispatch(failureWishlist(errorToString ? err.toString() : err))
      }
    }

export const createInWishlist =
  ({ id, modelId, onSuccess, onError, mindboxDeviceUUID, errorToString = true }) =>
    async (dispatch, getState, { api }) => {
      dispatch(startUpdatingWishlist())
      try {
        const resp = await api.addToWishlist({ product_id: id, model_id: modelId, mindboxDeviceUUID })
        const { result = 'success' } = resp
        if (result === 'success') {
          if (onSuccess) onSuccess()
          dispatch(successUpdatingWishlist())
        } else {
          dispatch(failureUpdatingWishlist(resp))
          onError?.(resp)
        }
      } catch (err) {
        dispatch(failureUpdatingWishlist(errorToString ? err.toString() : err))
        onError?.(err)
      }
    }

export const updateProductInWishlist =
  ({ id, modelId, newId, favoriteId, onSuccess, onError, mindboxDeviceUUID }) =>
    async (_dispatch, _getState, { api }) => {
      try {
        const resp = await api.updateWishlist({ model_id: modelId, product_id: id, new_product_id: newId, mindboxDeviceUUID, favorite_id: favoriteId })
        const { result = 'success' } = resp
        if (result === 'success') {
          if (onSuccess) onSuccess(resp)
        } else {
          onError?.(resp)
        }
      } catch (err) {
        onError?.(err)
      }
    }

export const removeFromWishlist =
  ({ id, modelId, favoriteId, onSuccess, onError, mindboxDeviceUUID, errorToString = true }) =>
    async (dispatch, _getState, { api }) => {
      dispatch(startUpdatingWishlist())
      try {
        const resp = await api.removeFromWishlist({ product_id: id, model_id: modelId, mindboxDeviceUUID, favorite_id: favoriteId })
        const { result = 'success' } = resp

        if (result === 'success') {
          if (onSuccess) onSuccess()
          dispatch(successUpdatingWishlist())
        } else {
          dispatch(failureUpdatingWishlist(resp))
          onError?.(resp)
        }
      } catch (err) {
        dispatch(failureUpdatingWishlist(errorToString ? err.toString() : err))
        onError?.(err)
      }
    }

export const updateWishlistIds =
  ({ ids }) =>
    (dispatch, _getState) => {
      dispatch(updateIds(ids))
    }
    
export const removeWishlistId =
  ({ id, isVariation }) =>
    (dispatch, getState) => {
      const state = getState().wishlist
      const ids = state.ids

      dispatch(updateIds(ids.filter(item => isVariation ? item.product_id != id : item.model_id != id)))
    }

export const addWishlistId =
  ({ id }) =>
    (dispatch, getState) => {
      const state = getState().wishlist
      const ids = state.ids

      dispatch(updateIds([...ids, id]))
    }

export const getWishlistData = createSelector(
  state => state.wishlist,
  wishlistState => {
    const { products, loading, updating, error, count, meta, links, ids, idsObj, firstLoading } = wishlistState
    return { products, loading, updating, error, count, meta, links, ids, idsObj, firstLoading }
  },
)

export const {
  requestWishlist,
  successWishlist,
  firstRequestWishlist,
  successRequestWishlist,
  failureWishlist,
  cancelSync,
  updateIds,
  updateIdsObj,
  resetWishlist,
  startUpdatingWishlist,
  successUpdatingWishlist,
  failureUpdatingWishlist,
} = wishlist.actions

export default wishlist.reducer
